
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EpisodeCategory } from "../episodes/types";

interface CategoryInsert {
  name: string;
  description?: string;
  color?: string;
  display_order?: number;
  status?: "draft" | "published" | "archived";
}

export const useCategories = () => {
  const [categories, setCategories] = useState<EpisodeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchCategories = async () => {
    try {
      console.log("Fetching episode categories...");
      const { data, error } = await supabase
        .from("episodes_categories")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) {
        console.error("Error fetching categories:", error);
        throw error;
      }
      
      console.log("Categories fetched:", data?.length || 0);
      setCategories(data || []);
    } catch (error: any) {
      console.error("Categories fetch error:", error);
      toast({
        title: "Error",
        description: "Failed to fetch categories: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveCategory = async (category: CategoryInsert, editingCategoryId?: string) => {
    try {
      console.log("Saving category:", { category, editingCategoryId });
      
      if (editingCategoryId) {
        // Update existing category
        const { error } = await supabase
          .from("episodes_categories")
          .update(category)
          .eq("id", editingCategoryId);

        if (error) {
          console.error("Error updating category:", error);
          throw error;
        }
        
        console.log("Category updated successfully");
        toast({ title: "Success", description: "Category updated successfully!" });
      } else {
        // Create new category
        const { error } = await supabase
          .from("episodes_categories")
          .insert(category);

        if (error) {
          console.error("Error creating category:", error);
          throw error;
        }
        
        console.log("Category created successfully");
        toast({ title: "Success", description: "Category created successfully!" });
      }

      // Refresh categories list
      await fetchCategories();
    } catch (error: any) {
      console.error("Save category error:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteCategory = async (categoryId: string) => {
    try {
      console.log("Deleting category:", categoryId);
      
      const { error } = await supabase
        .from("episodes_categories")
        .delete()
        .eq("id", categoryId);

      if (error) {
        console.error("Error deleting category:", error);
        throw error;
      }
      
      console.log("Category deleted successfully");
      toast({ title: "Success", description: "Category deleted successfully!" });
      
      // Refresh categories list
      await fetchCategories();
    } catch (error: any) {
      console.error("Delete category error:", error);
      toast({
        title: "Error",
        description: "Failed to delete category: " + error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    saveCategory,
    deleteCategory,
    refetch: fetchCategories,
  };
};
